import {
  Badge,
  Box,
  Text,
  Divider,
  ToggleButton,
  Card,
  CloseButton,
  Popover,
  FloatingNotification,
} from '@wix/design-system';
import { FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Smile } from '@wix/wix-ui-icons-common';
import { ArticleHelpfulState } from '@wix/answers-api';
import classNames from 'classnames';
import { voteForGeneratedAnswerInHelpCenterSrc4Evid601 } from '@wix/bi-logger-customer-care-data/v2';
import { GenAiArticleLink, PageType } from '../../../../../types';
import { DATA_HOOKS } from '../../../../../dataHooks';
import { useTranslation } from '@wix/wix-i18n-config';
import { unhelpfulFeedbackTypes } from '../..';
import { ClickOnResultBI, useSearchBarBI } from '../../../../SearchBar/bi';
import { BI } from '../../../../../constants';
import { Context } from '../../../../../context';
import { useBI } from '../../../../../hooks/useBI';
import { cleanGeneratedAnswer, mapPageTypeToSourceName } from '../../../../../utils/bi';
import { useRouter } from 'next/router';
import { mockArticleLinks } from '../../../../../__mocks__/gen-answers';
import { MoreInfoArticles } from './MoreInfoArticles';
import css from './index.module.scss';

export type GenAnswerFooterProps = {
  moreInfoArticles: GenAiArticleLink[];
  question: string;
  answerWordCount: number;
  searchAnswer: string;
};

const THANK_YOU_NOTIFICATION_TIMEOUT = 3000;

export const GenAnswerFooter: FunctionComponent<GenAnswerFooterProps> = ({
  moreInfoArticles = mockArticleLinks, question, answerWordCount, searchAnswer
}) => {
  const [helpfulState, setHelpfulState] = useState<ArticleHelpfulState>(ArticleHelpfulState.NONE);
  const [responded, setResponded] = useState<boolean>(false);
  const [showUnhelpfulFeedbackTypes, setShowUnhelpfulFeedbackTypes] = useState<boolean>(false);
  const [popoverShown, setPopoverShown] = useState<boolean>(false);
  const [selectedUnhelpfulReason, setSelectedUnhelpfulReason] = useState<unhelpfulFeedbackTypes>();
  const [showThankYouNotification, setShowThankYouNotification] = useState<boolean>(false);
  const { searchSessionId, sessionBI } = useContext(Context);
  const { locale } = useRouter();
  const { sendBIEvent } = useBI();
  const { clickOnResultBI } = useSearchBarBI();
  const { t } = useTranslation();

  useEffect(() => {
    setHelpfulState(ArticleHelpfulState.NONE);
    setResponded(false);
    setShowUnhelpfulFeedbackTypes(false);
    setPopoverShown(false);
    setSelectedUnhelpfulReason(undefined);
  }, [question]);

  useEffect(() => {
    if (responded) {
      setPopoverShown(true);
    }
  }, [responded]);

  useEffect(() => {
    if (selectedUnhelpfulReason) {
      const timer = setTimeout(() => {
        setShowThankYouNotification(false);
      }, THANK_YOU_NOTIFICATION_TIMEOUT);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedUnhelpfulReason]);

  const toggleHelpfulState = async (state: ArticleHelpfulState) => {
    if (responded) {
      if (helpfulState === state) {
        unSelectSelf();
      } else {
        if (state === ArticleHelpfulState.HELPFUL) {
          setPopoverShown(true);
        }
        setHelpfulState(state);
      }
    } else {
      setHelpfulState(state);
      setResponded(true);
      if (state === ArticleHelpfulState.HELPFUL) {
        setPopoverShown(true);
      }
    }
    await clickOnHelpfulOrNotButtonBI(state);
  };

  const unSelectSelf = () => {
    setHelpfulState(ArticleHelpfulState.NONE);
    setResponded(false);
  };

  const moreInfoArticlesToJSON = () => moreInfoArticles.map((articleLink) => ({
    article_id: articleLink.articleId,
    output_distance: articleLink.distance,
  })
  );

  const clickOnHelpfulOrNotButtonBI = useCallback(
    async (
      feedbackType: ArticleHelpfulState,
    ) => {
      void sendBIEvent(
        voteForGeneratedAnswerInHelpCenterSrc4Evid601({
          question,
          kb_lang: locale as string,
          search_method: BI.SEARCH_METHOD.KNOWLEDGE_AGENT,
          search_session_Id: searchSessionId,
          clicked_item_type: feedbackType === ArticleHelpfulState.HELPFUL ?
            BI.ITEM_TYPES.SUMMARIZED_ANSWER_HELPFUL : feedbackType === ArticleHelpfulState.NOT_HELPFUL ?
              BI.ITEM_TYPES.SUMMARIZED_ANSWER_NOT_HELPFUL : undefined,
          source_name: mapPageTypeToSourceName(PageType.SearchResults),
          self_service_session_id: sessionBI.selfServiceSessionId || undefined,
          summarized_answer_based_on: JSON.stringify(moreInfoArticlesToJSON()),
          summarized_answer_words_count: answerWordCount,
          summarized_answer_text: cleanGeneratedAnswer(searchAnswer)
        })
      );
    },
    [answerWordCount, locale, moreInfoArticlesToJSON, question, searchAnswer, searchSessionId, sendBIEvent, sessionBI.selfServiceSessionId]
  );

  const clickOnNotHelpfulReasonBI = useCallback(
    async (
      reason: unhelpfulFeedbackTypes
    ) => {
      void sendBIEvent(
        voteForGeneratedAnswerInHelpCenterSrc4Evid601({
          question,
          kb_lang: locale as string,
          search_method: BI.SEARCH_METHOD.KNOWLEDGE_AGENT,
          search_session_Id: searchSessionId,
          clicked_item_type: BI.ITEM_TYPES.SUMMARIZED_ANSWER_NOT_HELPFUL_REASON,
          clicked_item_name: reason,
          source_name: mapPageTypeToSourceName(PageType.SearchResults),
          self_service_session_id: sessionBI.selfServiceSessionId || undefined,
          summarized_answer_based_on: JSON.stringify(moreInfoArticlesToJSON()),
          summarized_answer_words_count: answerWordCount,
          summarized_answer_text: cleanGeneratedAnswer(searchAnswer)
        })
      );
    },
    [answerWordCount, locale, moreInfoArticlesToJSON, question, searchAnswer, searchSessionId, sendBIEvent, sessionBI.selfServiceSessionId]
  );

  const onMoreInfoArticleClick = async (index: number) => {
    const biArticleClickParams: ClickOnResultBI = {
      url: moreInfoArticles[index].articleURL,
      index,
      resultSourceType: moreInfoArticles[index].type,
      inputText: question,
      pageType: PageType.SearchResults,
      clickedItemId: moreInfoArticles[index].articleId,
      clickedItemType: BI.ITEM_TYPES.SUMMARIZED_ANSWER_BASED_ARTICLE,
      docType: moreInfoArticles[index].docType,
      searchMethod: BI.SEARCH_METHOD.KNOWLEDGE_AGENT,
      genAnswerBasedOnArticles: JSON.stringify(moreInfoArticlesToJSON()),
      genAnswerWordsCount: answerWordCount,
      genAnswer: cleanGeneratedAnswer(searchAnswer),
      selfServiceSessionId: sessionBI.selfServiceSessionId || undefined,
      searchResultSelection: BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
    };
    await clickOnResultBI(biArticleClickParams);
  };


  const onUnhelpfulFeedbackClick = async () => {
    setShowUnhelpfulFeedbackTypes(true);
    setShowThankYouNotification(false);
    await toggleHelpfulState(ArticleHelpfulState.NOT_HELPFUL);
  };

  const onHelpfulFeedbackClick = async () => {
    setShowUnhelpfulFeedbackTypes(false);
    setShowThankYouNotification(true);
    setSelectedUnhelpfulReason(undefined);
    await toggleHelpfulState(ArticleHelpfulState.HELPFUL);
  };

  const onUnhelpfulFeedbackReasonClick = async (reason: unhelpfulFeedbackTypes) => {
    setSelectedUnhelpfulReason(reason);
    setShowThankYouNotification(true);
    await clickOnNotHelpfulReasonBI(reason);
  };

  return (
    <Box direction="vertical" dataHook={DATA_HOOKS.GEN_ANSWER_FOOTER}>
      <Box className={css.footerMoreInfoAndVoting}>
        {moreInfoArticles.length > 0 &&
          <MoreInfoArticles moreInfoArticles={moreInfoArticles} onMoreInfoArticleClick={onMoreInfoArticleClick}></MoreInfoArticles>}
        <Box direction="horizontal" className={classNames(css.nextGenAnswerFooterInner, { [css.noMoreInfoArticlesShorter]: moreInfoArticles.length === 0 })}>
          <Box className={classNames(css.genAnswerFeedback, { [css.noMoreInfoArticlesLift]: moreInfoArticles.length === 0 })}>
            <Divider className={css.verticalDivider} direction="vertical" />
            <Box className={css.feedbackOptionsWrapper} direction="vertical">
              <Box className={css.feedbackOptions} dataHook={DATA_HOOKS.GEN_ANSWER_VOTING}>
                <ToggleButton selected={helpfulState === ArticleHelpfulState.HELPFUL}
                  dataHook={DATA_HOOKS.GEN_ANSWER_HELPFUL}
                  shape="round" border={true} onClick={onHelpfulFeedbackClick}
                  labelValue={t('search-results-page.gen-answer.helpful')}
                  labelPlacement="tooltip"
                  tooltipProps={{ disabled: popoverShown }}>
                  <ThumbsUp></ThumbsUp>
                </ToggleButton>
                <Popover
                  placement='top'
                  appendTo="window"
                  shown={popoverShown}
                  onShow={() => {
                    setTimeout(() => {
                      setPopoverShown(false);
                    }, 2000);
                  }
                  }
                >
                  <Popover.Element>
                    <ToggleButton
                      selected={helpfulState === ArticleHelpfulState.NOT_HELPFUL}
                      dataHook={DATA_HOOKS.GEN_ANSWER_NOT_HELPFUL}
                      shape="round" border={true}
                      onClick={onUnhelpfulFeedbackClick}
                      labelValue={t('search-results-page.gen-answer.unhelpful')}
                      labelPlacement="tooltip"
                      tooltipProps={{ disabled: popoverShown }}>
                      <ThumbsDown></ThumbsDown>
                    </ToggleButton>
                  </Popover.Element>
                  <Popover.Content>
                    {showThankYouNotification && helpfulState === ArticleHelpfulState.HELPFUL ? (
                      <Box className={css.popoverFeedback} padding="10px">
                        {t('search-results-page.gen-answer.helped')}
                      </Box>) : null}
                  </Popover.Content>
                </Popover>
              </Box>
            </Box>
          </Box>
        </Box >
      </Box>
      {showUnhelpfulFeedbackTypes && helpfulState === ArticleHelpfulState.NOT_HELPFUL &&
        <Card
          className={classNames(css.notHelpfulFeedbackCard, { [css.withFloatingNotification]: showThankYouNotification })}
          dataHook={DATA_HOOKS.GEN_ANSWER_UNHELPFUL_REASONS}
          controls={<CloseButton size="large" skin="dark"
            onClick={() => setShowUnhelpfulFeedbackTypes(false)} />}>
          <Text className={css.title} weight='normal'>
            {t('search-results-page.gen-answer.why-unhelpful')} <span className={css.optional}>(optional)</span>
          </Text>
          <Box direction="horizontal" gap='9px' >
            {Object.values(unhelpfulFeedbackTypes).map((type, index) => (
              <Badge
                className={classNames(css.badge, { [css.selected]: selectedUnhelpfulReason === type })}
                skin="neutralStandard"
                type={selectedUnhelpfulReason === type ? 'solid' : 'outlined'}
                onClick={() => onUnhelpfulFeedbackReasonClick(type)}
                uppercase={false} key={index}
                dataHook={DATA_HOOKS.GEN_ANSWER_UNHELPFUL_BADGE}>
                {t(`search-results-page.gen-answer.reason.${type}`)}
              </Badge>
            ))}
          </Box>
          {showThankYouNotification ? (
            <FloatingNotification dataHook={DATA_HOOKS.GEN_ANSWER_UNHELPFUL_REASONS_FLOATING_NOTIFICATION}
              type='preview' prefixIcon={<Smile />} className={css.floatingNotification}
              onClose={() => setShowThankYouNotification(false)}
              text={t('search-results-page.gen-answer.unhelpful-feedback-submitted')} />
          ) : null}
        </Card>
      }
    </Box >
  );
};
